import api from '../api/gtfsStaticApi';
import { RequestParameters } from '../types/apiTypes';
import { FullRouteInfo, GtfsDirectionVariantModel, GtfsRoute, GtfsStopTimeEntity, GtfsTripAndStops, RouteMapGeometryRecord, ShapePoint, Trip, TripsRequest } from '../types/gtfsTypes';
import { KeyValuePair } from '../types/types';

function normalizeRouteIds(routeIds: string[]) {
    return Array.from(new Set((routeIds || [])
        .map(routeId => (routeId || '').trim())
        .filter(routeId => routeId.length > 0)))
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
}

function toNumber(value: any): number | undefined {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeRouteMapGeometryRows(rows: any[] | null | undefined): RouteMapGeometryRecord[] {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.map((route: any) => {
        const directionsRaw = route.directions ?? route.Directions;
        const directions = Array.isArray(directionsRaw) ? directionsRaw : [];

        return {
            routeId: String(route.routeId ?? route.RouteId ?? ''),
            routeName: String(route.routeName ?? route.RouteName ?? ''),
            routeColor: String(route.routeColor ?? route.RouteColor ?? ''),
            directions: directions.map((direction: any) => {
                const directionVariantsRaw = direction.directionVariants ?? direction.DirectionVariants;
                const directionVariants = Array.isArray(directionVariantsRaw) ? directionVariantsRaw : [];

                return {
                    directionId: String(direction.directionId ?? direction.DirectionId ?? ''),
                    directionVariants: directionVariants.map((variant: any) => {
                        const shapePointsRaw = variant.shapePoints ?? variant.ShapePoints;
                        const shapePoints = Array.isArray(shapePointsRaw) ? shapePointsRaw : [];
                        const stopsRaw = variant.stops ?? variant.Stops;
                        const stops = Array.isArray(stopsRaw) ? stopsRaw : [];

                        return {
                            directionVariantId: String(variant.directionVariantId ?? variant.DirectionVariantId ?? ''),
                            shapePoints: shapePoints.map((point: any) => ({
                                lat: (toNumber(point.lat ?? point.Lat) ?? 0) as Latitude,
                                lon: (toNumber(point.lon ?? point.Lon) ?? 0) as Longitude,
                            })),
                            stops: stops.map((stop: any) => ({
                                stopId: String(stop.stopId ?? stop.StopId ?? ''),
                                stopCode: String(stop.stopCode ?? stop.StopCode ?? ''),
                                stopName: String(stop.stopName ?? stop.StopName ?? ''),
                                lat: (toNumber(stop.lat ?? stop.Lat) ?? 0) as Latitude,
                                lon: (toNumber(stop.lon ?? stop.Lon) ?? 0) as Longitude,
                            })),
                        };
                    }),
                };
            }),
        };
    });
}

export async function getRoutes(agencyId: string, date?: string): Promise<GtfsRoute[]> {
    const result = await api.getRoutes(agencyId, date);
    return result.data || [];
}
export async function getRoutesPairs(agencyId: string, date?: string): Promise<KeyValuePair<string, string>[]> {
    const result = await api.getRoutes(agencyId, date);
    return result.data ? result.data.map(d => ({ key: d.routeId, value: d.routeName })) : [];
}
export async function getRoutesPairs2(agencyId: string, date?: string): Promise<KeyValuePair<string, string>[]> {
    const result = await api.getRoutes(agencyId, date);
    return result.data ? result.data.map(d => ({ key: d.routeId, value: d.routeName + ' ' +d.longName })) : [];
}
export async function getRoutesPairsWithLongName(
    agencyId: string,
    date?: string,
): Promise<{ routeId: string; routeName: string; longName: string; serviceType?: string }[]> {
    const result = await api.getRoutes(agencyId, date);
    return (result.data || []).map(d => ({
        routeId: d.routeId,
        routeName: d.routeName,
        longName: d.longName,
        serviceType: d.serviceType,
    }));
}
export async function getDirections(routeId: string) {
    const result = await api.getDirections(routeId);
    return result.data || [];
}
export async function getFullRouteInfo(routeId: string): Promise<FullRouteInfo> {
    const result = await api.getFullRouteInfo(routeId);
    return result.data;
}
export async function getFullRouteInfoByRouteName(agencyId: string, routeName: string): Promise<FullRouteInfo> {
    const result = await api.getFullRouteInfoByRouteName(agencyId, routeName);
    return result.data;
}
export async function getRouteMapGeometryByRoutes(routeIds: string[], parameters?: RequestParameters): Promise<RouteMapGeometryRecord[]> {
    const normalizedRouteIds = normalizeRouteIds(routeIds);
    if (normalizedRouteIds.length === 0) {
        return [];
    }

    const result = await api.getRouteMapGeometryByRoutes(normalizedRouteIds, parameters);
    return normalizeRouteMapGeometryRows(result.data as any);
}
export async function getTripsForDateAndRoute(request: TripsRequest): Promise<Trip[]> {
    const result = await api.getTripsForDateAndRoute(request);
    return result.data || [];
}
export async function getTripsForTimeAndDirection(request: TripsRequest) {
    const result = await api.getTripsForTimeAndDirection(request);
    return result.data || [];
}
export async function getTripsForDateAndDirection(request: TripsRequest) {
    const result = await api.getTripsForDateAndDirection(request);
    return result.data || [];
}
export async function getTripsForDateAndDirectionVariant(request: TripsRequest) {
    const result = await api.getTripsForDateAndDirectionVariant(request);
    return result.data || [];
}
export async function getShapeData(agencyId: string, serviceDate: string, shapeId: string): Promise<ShapePoint[]> {
    const result = await api.getShapeData(agencyId, serviceDate, shapeId);
    return result.data || [];
}
export async function getSparkTripsByDirectionVariantIntertnalId(agencyId: string, serviceDate: string, directionVariantInternalId: string) {
    const result = await api.getSparkTripsByDirectionVariantIntertnalId(agencyId, serviceDate, directionVariantInternalId);
    return result.data || [];
}
export async function getGtfsStopTimesByStopAndShape(agencyId: string, serviceDate: string, shapeId: string, stopId: string): Promise<GtfsStopTimeEntity[]> {
    const result = await api.getGtfsStopTimesByStopAndShape(agencyId, serviceDate, shapeId, stopId);
    return result.data || [];
}
export async function getGtfsTripsAndStops(agencyId: string, reportDate: string): Promise<GtfsTripAndStops[]> {
    const result = await api.getGtfsTripsAndStops(agencyId, reportDate);
    return result.data || [];
}
export async function getGtfsDirectionVariants(agencyId: string, startDate: string, endDate: string): Promise<GtfsDirectionVariantModel[]> {
    const result = await api.getGtfsDirectionVariants(agencyId, startDate, endDate);
    return result.data || [];
}
export async function getDirectionVariantsByRoute(routeId: string, time: string, serviceDate?: string) {
    const result = await api.getDirectionVariantsByRoute(routeId, time, serviceDate);
    return result.data || [];
}
export async function getDirectionVariantsForStop(agencyId: string, stopId: string, routeId: string | null = null, time: string | null = null, serviceDate?: string) {
    const result = await api.getDirectionVariantsForStop(agencyId, stopId, routeId, time, serviceDate);
    return result.data || [];
}
export async function getGtfsRoutes(agencyId: string): Promise<string[]> {
    const result = await api.getGtfsRoutes(agencyId);
    return result.data || [];
}
export async function getAgencyLocation(agencyId: string) {
    return await api.getAgencyLocation(agencyId);
}
